import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { updateCustomer, getCustomerById } from './customerService';

// نوع معاملة الرصيد
export enum CreditTransactionType {
  REFUND = 'refund',     // إضافة رصيد من مرتجع
  PURCHASE = 'purchase', // خصم رصيد من شراء
  ADJUSTMENT = 'adjustment' // تعديل يدوي
}

// واجهة معاملة الرصيد
export interface CreditTransaction {
  id: string;
  customerId: string;
  customerName: string;
  type: CreditTransactionType;
  amount: number; // موجب للإضافة، سالب للخصم
  description: string;
  referenceId?: string; // معرف المرتجع أو الفاتورة
  employeeId: string;
  employeeName: string;
  branchId: string;
  branchName: string;
  createdAt: Date | string;
  notes?: string;
}

/**
 * إضافة رصيد للعميل من مرتجع
 */
export const addCustomerCreditFromReturn = async (
  customerId: string,
  amount: number,
  returnId: string,
  employeeId: string,
  employeeName: string,
  branchId: string,
  branchName: string,
  notes?: string
): Promise<void> => {
  try {
    console.log(`💰 إضافة رصيد ${amount} للعميل ${customerId} من المرتجع ${returnId}`);

    // الحصول على بيانات العميل
    const customer = await getCustomerById(customerId);
    if (!customer) {
      throw new Error('العميل غير موجود');
    }

    // إنشاء معاملة الرصيد
    const transaction: Omit<CreditTransaction, 'id'> = {
      customerId,
      customerName: customer.name,
      type: CreditTransactionType.REFUND,
      amount,
      description: `رصيد من مرتجع فاتورة`,
      referenceId: returnId,
      employeeId,
      employeeName,
      branchId,
      branchName,
      createdAt: new Date(),
      notes: notes || ''
    };

    // حفظ المعاملة
    await addDoc(collection(db, 'customer_credit_transactions'), {
      ...transaction,
      createdAt: serverTimestamp()
    });

    // تحديث رصيد العميل
    const currentBalance = customer.creditBalance || 0;
    const newBalance = currentBalance + amount;

    await updateCustomer(customerId, {
      creditBalance: newBalance
    });

    console.log(`✅ تم إضافة رصيد ${amount} للعميل. الرصيد الجديد: ${newBalance}`);
  } catch (error) {
    console.error('Error adding customer credit from return:', error);
    throw error;
  }
};

/**
 * خصم رصيد من العميل للشراء
 */
export const deductCustomerCreditForPurchase = async (
  customerId: string,
  amount: number,
  saleId: string,
  employeeId: string,
  employeeName: string,
  branchId: string,
  branchName: string,
  notes?: string
): Promise<void> => {
  try {
    console.log(`💳 خصم رصيد ${amount} من العميل ${customerId} للشراء ${saleId}`);

    // الحصول على بيانات العميل
    const customer = await getCustomerById(customerId);
    if (!customer) {
      throw new Error('العميل غير موجود');
    }

    const currentBalance = customer.creditBalance || 0;
    if (currentBalance < amount) {
      throw new Error(`رصيد العميل غير كافي. الرصيد الحالي: ${currentBalance}`);
    }

    // إنشاء معاملة الرصيد
    const transaction: Omit<CreditTransaction, 'id'> = {
      customerId,
      customerName: customer.name,
      type: CreditTransactionType.PURCHASE,
      amount: -amount, // سالب للخصم
      description: `خصم رصيد لشراء فاتورة`,
      referenceId: saleId,
      employeeId,
      employeeName,
      branchId,
      branchName,
      createdAt: new Date(),
      notes: notes || ''
    };

    // حفظ المعاملة
    await addDoc(collection(db, 'customer_credit_transactions'), {
      ...transaction,
      createdAt: serverTimestamp()
    });

    // تحديث رصيد العميل
    const newBalance = currentBalance - amount;
    await updateCustomer(customerId, {
      creditBalance: newBalance
    });

    console.log(`✅ تم خصم رصيد ${amount} من العميل. الرصيد الجديد: ${newBalance}`);
  } catch (error) {
    console.error('Error deducting customer credit for purchase:', error);
    throw error;
  }
};

/**
 * الحصول على رصيد العميل
 */
export const getCustomerCreditBalance = async (customerId: string): Promise<number> => {
  try {
    const customer = await getCustomerById(customerId);
    return customer?.creditBalance || 0;
  } catch (error) {
    console.error('Error getting customer credit balance:', error);
    return 0;
  }
};

/**
 * الحصول على معاملات رصيد العميل
 */
export const getCustomerCreditTransactions = async (customerId: string): Promise<CreditTransaction[]> => {
  try {
    const transactionsQuery = query(
      collection(db, 'customer_credit_transactions'),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(transactionsQuery);
    const transactions: CreditTransaction[] = [];

    snapshot.forEach(doc => {
      const data = doc.data();
      transactions.push({
        id: doc.id,
        ...data,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date()
      } as CreditTransaction);
    });

    return transactions;
  } catch (error) {
    console.error('Error getting customer credit transactions:', error);
    return [];
  }
};

/**
 * تعديل رصيد العميل يدوياً
 */
export const adjustCustomerCredit = async (
  customerId: string,
  amount: number,
  reason: string,
  employeeId: string,
  employeeName: string,
  branchId: string,
  branchName: string,
  notes?: string
): Promise<void> => {
  try {
    console.log(`🔧 تعديل رصيد العميل ${customerId} بمقدار ${amount}`);

    // الحصول على بيانات العميل
    const customer = await getCustomerById(customerId);
    if (!customer) {
      throw new Error('العميل غير موجود');
    }

    // إنشاء معاملة التعديل
    const transaction: Omit<CreditTransaction, 'id'> = {
      customerId,
      customerName: customer.name,
      type: CreditTransactionType.ADJUSTMENT,
      amount,
      description: reason,
      employeeId,
      employeeName,
      branchId,
      branchName,
      createdAt: new Date(),
      notes: notes || ''
    };

    // حفظ المعاملة
    await addDoc(collection(db, 'customer_credit_transactions'), {
      ...transaction,
      createdAt: serverTimestamp()
    });

    // تحديث رصيد العميل
    const currentBalance = customer.creditBalance || 0;
    const newBalance = Math.max(0, currentBalance + amount); // لا يمكن أن يكون الرصيد سالب

    await updateCustomer(customerId, {
      creditBalance: newBalance
    });

    console.log(`✅ تم تعديل رصيد العميل. الرصيد الجديد: ${newBalance}`);
  } catch (error) {
    console.error('Error adjusting customer credit:', error);
    throw error;
  }
};
